import type { AbstractViewInterface, SceneLayerInterface } from '@navvis/ivion';

/** View that exposes at least one scene layer getter (map view and main view do). */
type ViewWithLayers = AbstractViewInterface & {
	getPoiLayer?: () => SceneLayerInterface | undefined;
	getRouteLayer?: () => SceneLayerInterface | undefined;
};

/** Internal IVION view reachable through any scene layer's `view` (not part of the public API). */
interface InternalView {
	layers?: SceneLayerInterface[];
}

/**
 * Removes the IVION built-in context menu entries ("Route to here", "Route
 * from here", "Crop & download", ...) from a view so only the entries of the
 * given custom layers remain.
 *
 * IVION merges the entries of every layer of the view, so a custom layer
 * cannot hide the others by itself. Instead the `onContextMenu` handler of
 * every other layer is replaced by a no-op. The layer list is only reachable
 * through the internal view (same trick as the distanceThreshold workaround
 * in src/index.ts); when it is not available at least the route layer,
 * which is exposed by the public API, is silenced. Idempotent, safe to call
 * after every site load.
 */
export function keepOnlyContextMenuLayers(view: ViewWithLayers, keep: SceneLayerInterface[]): void {
	const silence = (layer: SceneLayerInterface | undefined): void => {
		if (layer && !keep.includes(layer) && typeof layer.onContextMenu === 'function') {
			layer.onContextMenu = () => undefined;
		}
	};

	try {
		const anyLayer = view.getPoiLayer?.() ?? view.getRouteLayer?.();
		const layers = (anyLayer as unknown as { view?: InternalView } | undefined)?.view?.layers;
		if (layers && layers.length > 0) {
			layers.forEach(silence);
			return;
		}
	} catch (err) {
		console.warn('[IvionContextMenu] Could not enumerate view layers:', err);
	}

	// Fallback: the route layer is the only built-in layer with entries for a
	// regular user and it is reachable through the public API.
	silence(view.getRouteLayer?.());
}
